import React, { createContext, useContext } from 'react';
import { QueryClientProvider, useQueryClient } from '../lib/queryRuntime';
import { AuthProvider, useAuth } from './AuthContext';
import { AppDataProvider, useAppData } from './AppDataContext';
import { listingService } from '../services/listingService';
import { chatService } from '../services/chatService';
import { reservationService } from '../services/reservationService';
import { reviewService } from '../services/reviewService';
import { queryClient, queryKeys } from '../lib/queryClient';

const ShareContext = createContext(null);

const ShareBridge = ({ children }) => {
  const auth = useAuth();
  const data = useAppData();
  const cache = useQueryClient();
  const { user } = auth;

  const refreshPublic = () => cache.invalidateQueries({ queryKey: queryKeys.publicRoot });
  const refreshPrivate = () => (user?.id
    ? cache.invalidateQueries({ queryKey: ['private-data'] })
    : Promise.resolve());
  const refreshAll = () => Promise.all([refreshPublic(), refreshPrivate()]);

  const value = {
    ...auth,
    ...data,
    refreshData: refreshAll,
    addListing: async (listing) => {
      if (!user?.id) throw new Error('Bitte melde dich erneut an.');
      const result = await listingService.create({
        ...listing,
        providerId: user.id,
        providerName: listing.isAnonymous ? 'Anonym' : user.name,
      });
      await refreshPublic();
      return result;
    },
    updateListing: async (id, update) => {
      const result = await listingService.update(id, update);
      await refreshPublic();
      return result;
    },
    deleteListing: async (id) => {
      await listingService.remove(id);
      await refreshPublic();
    },
    markAsGone: async (id) => {
      await listingService.update(id, { status: 'gone', quantity: 0 });
      await refreshAll();
    },
    sendMessage: async (chatId, text) => {
      if (!user?.id) throw new Error('Bitte melde dich erneut an.');
      const message = await chatService.sendMessage(chatId, user.id, text);
      await refreshPrivate();
      return message;
    },
    createChat: async (listing) => {
      const chat = await chatService.createChat(listing);
      await refreshPrivate();
      return chat;
    },
    startChat: async (listing) => {
      const chat = await chatService.createChat(listing);
      await refreshPrivate();
      return chat.id;
    },
    markChatRead: async (chatId) => {
      if (!user?.id) return null;
      const readAt = await chatService.markRead(chatId);
      cache.setQueryData(queryKeys.chats(user.id), (current = []) => current.map((chat) => {
        if (String(chat.id) !== String(chatId)) return chat;
        const isProvider = String(chat.providerId) === String(user.id);
        return {
          ...chat,
          provider_read_at: isProvider ? readAt : chat.provider_read_at,
          requester_read_at: isProvider ? chat.requester_read_at : readAt,
          readAt,
          unreadCount: 0,
        };
      }));
      return readAt;
    },
    createReservation: async (payload) => {
      const result = await reservationService.create(payload);
      await refreshAll();
      return result;
    },
    updateReservationStatus: async (reservationId, status) => {
      const result = await reservationService.updateStatus(reservationId, status);
      await refreshAll();
      return result;
    },
    reserveItems: async (chatId, listingId, amount) => {
      const result = await reservationService.create({ listingId, quantity: amount, chatId });
      await refreshAll();
      return result;
    },
    addReview: async (review) => {
      if (!user?.id) throw new Error('Bitte melde dich erneut an.');
      const result = await reviewService.create({ ...review, requesterId: user.id });
      await refreshPublic();
      return result;
    },
    calculateUserPoints: (userId) => {
      const userListings = data.listings.filter((listing) => String(listing.providerId) === String(userId));
      const listingPoints = userListings.length * 10;
      const togetherPoints = userListings.filter((listing) => listing.eatTogether || listing.eat_together).length * 5;
      const reviewPoints = data.reviews
        .filter((review) => String(review.providerId || review.provider_id) === String(userId))
        .reduce((total, review) => {
          const taste = Number(review.taste) || 0;
          const reliability = Number(review.reliability) || 0;
          return total + ((taste + reliability) / 2 >= 4 ? 2 : 0);
        }, 0);
      return listingPoints + togetherPoints + reviewPoints;
    },
  };

  return <ShareContext.Provider value={value}>{children}</ShareContext.Provider>;
};

export const ShareProvider = ({ children }) => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <AppDataProvider>
        <ShareBridge>{children}</ShareBridge>
      </AppDataProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export const useShare = () => {
  const value = useContext(ShareContext);
  if (!value) throw new Error('useShare muss innerhalb des ShareProvider verwendet werden.');
  return value;
};
